// Asset Intelligence Automation Data
// Supports: Intent in, PR out, pipeline applies model

// ============================================================================
// SUGGESTED AUTOMATION ACTIONS
// ============================================================================
export const assetAutomationSuggestions = [
  {
    id: 'refresh-servers-3yr',
    title: 'Refresh 3 servers past 3-year policy',
    description: 'db-server-01, app-server-12, legacy-web-03 exceed hardware refresh policy',
    impact: {
      timeSaved: '40 hours manual work',
      costSavings: '$45,000 replacement budget',
      riskReduction: 'Eliminates hardware failure risk'
    },
    riskLevel: 'medium',
    requiredApprovals: ['Data Team Lead', 'Platform Lead', 'Web Team Lead'],
    targets: ['db-server-01', 'app-server-12', 'legacy-web-03'],
    changeSpecType: 'hardware_refresh',
    automationType: 'refresh'
  },
  {
    id: 'migration-plan-active',
    title: 'Create migration plan for active workloads',
    description: '2 servers have active workloads requiring migration before decommission',
    impact: {
      timeSaved: '20 hours planning',
      costSavings: 'Avoid downtime costs',
      riskReduction: 'Zero-downtime migration'
    },
    riskLevel: 'high',
    requiredApprovals: ['Data Team Lead', 'Web Team Lead', 'Change Advisory Board'],
    targets: ['db-server-01', 'legacy-web-03'],
    changeSpecType: 'migration_plan',
    automationType: 'migration'
  },
  {
    id: 'reclaim-saas-seats',
    title: 'Reclaim inactive SaaS seats',
    description: 'Figma, Notion, Miro have 241 inactive seats - $51K/year savings',
    impact: {
      timeSaved: '10 hours license management',
      costSavings: '$51,000/year',
      riskReduction: 'Reduce license compliance risk'
    },
    riskLevel: 'low',
    requiredApprovals: ['IT Manager', 'Finance'],
    targets: ['figma', 'notion', 'miro'],
    changeSpecType: 'saas_reclaim',
    automationType: 'reclaim'
  },
  {
    id: 'renewal-review-workflow',
    title: 'Create renewal review workflow',
    description: 'Upcoming renewals: Datadog ($340K), GitHub ($89K), Snowflake ($520K)',
    impact: {
      timeSaved: '15 hours negotiation prep',
      costSavings: 'Potential 10-15% discount',
      riskReduction: 'Avoid auto-renewal at current rates'
    },
    riskLevel: 'low',
    requiredApprovals: ['IT Manager', 'Finance', 'Procurement'],
    targets: ['datadog', 'github-enterprise', 'snowflake'],
    changeSpecType: 'renewal_review',
    automationType: 'renewal'
  },
  {
    id: 'create-exception-template',
    title: 'Create exception with expiry',
    description: 'Document policy exception for any asset with owner and expiry date',
    impact: {
      timeSaved: '2 hours documentation',
      costSavings: 'N/A',
      riskReduction: 'Audit trail for exceptions'
    },
    riskLevel: 'low',
    requiredApprovals: ['Asset Owner', 'Security Team'],
    targets: [],
    changeSpecType: 'lifecycle_exception',
    automationType: 'exception'
  }
];

// ============================================================================
// ASSET DEPENDENCY GRAPH
// ============================================================================
export const assetDependencyGraph = {
  'db-server-01': {
    upstream: [],
    downstream: [
      { id: 'payments-db', name: 'payments-db', type: 'Database', criticality: 'P1' },
      { id: 'checkout-service', name: 'checkout-service', type: 'Service', criticality: 'P1' },
      { id: 'order-service', name: 'order-service', type: 'Service', criticality: 'P1' }
    ],
    blastRadius: 'high',
    riskLabel: 'Revenue-impacting',
    workloads: ['PostgreSQL primary', 'Payments processing'],
    migrationComplexity: 'high'
  },
  'app-server-12': {
    upstream: [],
    downstream: [
      { id: 'api-gateway', name: 'api-gateway', type: 'Service', criticality: 'P2' },
      { id: 'batch-processor', name: 'batch-processor', type: 'Service', criticality: 'P3' }
    ],
    blastRadius: 'medium',
    riskLabel: 'Internal services',
    workloads: ['API routing', 'Background jobs'],
    migrationComplexity: 'medium'
  },
  'legacy-web-03': {
    upstream: [],
    downstream: [
      { id: 'legacy-portal', name: 'legacy-portal', type: 'Web App', criticality: 'P2' },
      { id: 'admin-dashboard', name: 'admin-dashboard', type: 'Web App', criticality: 'P3' }
    ],
    blastRadius: 'medium',
    riskLabel: 'Internal tools',
    workloads: ['Legacy customer portal', 'Admin tools'],
    migrationComplexity: 'high'
  }
};

// ============================================================================
// CHANGE SPECS FOR ASSET OPERATIONS
// ============================================================================
export const assetChangeSpecs = {
  'refresh-servers-3yr': {
    id: 'CS-2026-0090',
    title: 'Hardware Refresh - 3 Servers Past Policy',
    type: 'hardware_refresh',
    version: '1.0',
    targets: [
      { id: 'db-server-01', name: 'db-server-01', owner: 'Data Team', age: '4.2 yrs' },
      { id: 'app-server-12', name: 'app-server-12', owner: 'Platform', age: '3.8 yrs' },
      { id: 'legacy-web-03', name: 'legacy-web-03', owner: 'Web Team', age: '5.1 yrs' }
    ],
    constraints: {
      maintenance_window_required: true,
      rollback_required: true,
      dry_run: true
    },
    risk: 'medium',
    required_approvals: ['Data Team Lead', 'Platform Lead', 'Web Team Lead'],
    generated_artifacts: {
      plan: 'hardware-refresh-plan.yaml',
      policy_simulation: 'policy-sim-output.json',
      tests: 'refresh-tests.yaml'
    },
    policy_checks: [
      { name: 'Hardware Age Policy', status: 'violation', note: 'All 3 servers exceed 3-year limit' },
      { name: 'Backup Verification', status: 'pass', note: 'All backups current' },
      { name: 'Migration Path', status: 'warning', note: '2 servers need workload migration' }
    ],
    audit: {
      event_schema_version: '1.0',
      created_at: '2026-01-11T14:00:00Z',
      created_by: 'Asset Intelligence'
    }
  },
  'migration-plan-active': {
    id: 'CS-2026-0091',
    title: 'Migration Plan - Active Workloads',
    type: 'migration_plan',
    version: '1.0',
    targets: [
      { id: 'db-server-01', name: 'db-server-01', workloads: ['PostgreSQL primary', 'Payments processing'] },
      { id: 'legacy-web-03', name: 'legacy-web-03', workloads: ['Legacy customer portal', 'Admin tools'] }
    ],
    constraints: {
      maintenance_window_required: true,
      rollback_required: true,
      dry_run: true,
      zero_downtime: true
    },
    risk: 'high',
    required_approvals: ['Data Team Lead', 'Web Team Lead', 'Change Advisory Board'],
    generated_artifacts: {
      plan: 'migration-runbook.yaml',
      policy_simulation: 'migration-policy-sim.json',
      tests: 'migration-tests.yaml'
    },
    policy_checks: [
      { name: 'Data Integrity', status: 'pass', note: 'Checksums will be validated' },
      { name: 'Service Continuity', status: 'pass', note: 'Blue-green deployment configured' },
      { name: 'Rollback Plan', status: 'pass', note: 'Automated rollback triggers defined' }
    ],
    audit: {
      event_schema_version: '1.0',
      created_at: '2026-01-11T14:00:00Z',
      created_by: 'Asset Intelligence'
    }
  },
  'reclaim-saas-seats': {
    id: 'CS-2026-0092',
    title: 'SaaS License Reclamation',
    type: 'saas_reclaim',
    version: '1.0',
    targets: [
      { id: 'figma', name: 'Figma', inactive_seats: 133, annual_savings: 24000 },
      { id: 'notion', name: 'Notion', inactive_seats: 266, annual_savings: 20000 },
      { id: 'miro', name: 'Miro', inactive_seats: 109, annual_savings: 7000 }
    ],
    constraints: {
      maintenance_window_required: false,
      rollback_required: true,
      dry_run: true,
      notification_period_days: 7
    },
    risk: 'low',
    required_approvals: ['IT Manager', 'Finance'],
    generated_artifacts: {
      plan: 'license-reclaim-plan.yaml',
      policy_simulation: 'license-policy-sim.json',
      tests: 'reclaim-tests.yaml'
    },
    policy_checks: [
      { name: 'User Notification', status: 'pass', note: '7-day warning period configured' },
      { name: 'License Policy', status: 'pass', note: 'Below 50% utilization threshold' },
      { name: 'Cost Approval', status: 'required', note: 'Finance sign-off for $51K savings' }
    ],
    audit: {
      event_schema_version: '1.0',
      created_at: '2026-01-11T14:00:00Z',
      created_by: 'Asset Intelligence'
    }
  },
  'exception-legacy-web': {
    id: 'CS-2026-0093',
    title: 'Policy Exception - legacy-web-03',
    type: 'lifecycle_exception',
    version: '1.0',
    targets: [
      { id: 'legacy-web-03', name: 'legacy-web-03', owner: 'Web Team', current_age: '5.1 yrs' }
    ],
    constraints: {
      maintenance_window_required: false,
      rollback_required: false,
      dry_run: false
    },
    risk: 'low',
    required_approvals: ['Web Team Lead', 'Security Team'],
    exception_details: {
      reason: 'Migration to cloud scheduled for Q2 2026',
      expiry_date: '2026-06-30',
      owner: 'David Kim',
      review_date: '2026-03-31'
    },
    generated_artifacts: {
      plan: 'exception-record.yaml',
      policy_simulation: 'exception-policy-sim.json',
      tests: null
    },
    policy_checks: [
      { name: 'Exception Justification', status: 'pass', note: 'Migration plan documented' },
      { name: 'Expiry Date', status: 'pass', note: 'Within 6-month limit' },
      { name: 'Owner Assignment', status: 'pass', note: 'Web Team Lead assigned' }
    ],
    audit: {
      event_schema_version: '1.0',
      created_at: '2026-01-11T14:00:00Z',
      created_by: 'Asset Intelligence'
    }
  }
};

// ============================================================================
// THREE-LAYER ARTIFACTS
// ============================================================================

// Connector Layer Artifacts
export const connectorCodegenArtifacts = {
  apiClientStub: `// Generated API Client - Asset Management Service
// OpenAPI Generated Style - DO NOT EDIT MANUALLY

import { HttpClient, RetryPolicy, PaginationHandler } from '@vedaya/sdk';

export interface AssetServiceConfig {
  baseUrl: string;
  authType: 'oauth2' | 'saml' | 'api_key' | 'service_principal';
  credentials: AuthCredentials;
}

export class AssetServiceClient {
  private http: HttpClient;
  private retry: RetryPolicy;
  private paginator: PaginationHandler;

  constructor(config: AssetServiceConfig) {
    this.http = new HttpClient(config.baseUrl);
    this.retry = new RetryPolicy({ maxAttempts: 3, backoff: 'exponential' });
    this.paginator = new PaginationHandler({ pageSize: 100 });
  }

  // Hardware Asset Operations
  async listHardwareAssets(filters?: AssetFilters): Promise<HardwareAsset[]> {
    return this.paginator.fetchAll('/api/v1/assets/hardware', filters);
  }

  async getAsset(assetId: string): Promise<HardwareAsset> {
    return this.retry.execute(() => this.http.get(\`/api/v1/assets/\${assetId}\`));
  }

  async updateAssetStatus(assetId: string, status: AssetStatus): Promise<void> {
    return this.retry.execute(() => 
      this.http.patch(\`/api/v1/assets/\${assetId}/status\`, { status })
    );
  }

  // SaaS License Operations
  async listSaaSLicenses(): Promise<SaaSLicense[]> {
    return this.paginator.fetchAll('/api/v1/licenses/saas');
  }

  async reclaimLicense(licenseId: string, userId: string): Promise<void> {
    return this.retry.execute(() =>
      this.http.post(\`/api/v1/licenses/\${licenseId}/reclaim\`, { userId })
    );
  }
}`,

  authHandlerStub: `// Generated Auth Handler - OAuth 2.0
// Supports: OAuth 2.0, SAML, API Key, Service Principal

import { OAuth2Handler, TokenStore } from '@vedaya/auth';

export class AssetServiceAuth extends OAuth2Handler {
  private tokenStore: TokenStore;

  constructor(config: OAuth2Config) {
    super({
      clientId: config.clientId,
      clientSecret: config.clientSecret,
      tokenUrl: 'https://asset-service.internal/oauth/token',
      scopes: ['assets:read', 'assets:write', 'licenses:manage'],
      grantType: 'client_credentials'
    });
    this.tokenStore = new TokenStore({ encryption: true });
  }

  async getAccessToken(): Promise<string> {
    const cached = await this.tokenStore.get('asset-service');
    if (cached && !this.isExpired(cached)) {
      return cached.accessToken;
    }
    const token = await this.refreshToken();
    await this.tokenStore.set('asset-service', token);
    return token.accessToken;
  }

  async revokeToken(): Promise<void> {
    await this.tokenStore.delete('asset-service');
    await super.revokeToken();
  }
}`,

  normalizerStub: `// Generated Normalizer - Vendor to Vedaya Schema
// Maps external asset formats to canonical Vedaya Asset schema

import { NormalizedAsset, VendorAsset, SchemaValidator } from '@vedaya/schema';

export class AssetNormalizer {
  private validator: SchemaValidator;

  constructor() {
    this.validator = new SchemaValidator('vedaya-asset-v1');
  }

  normalize(vendor: VendorAsset): NormalizedAsset {
    const normalized: NormalizedAsset = {
      id: vendor.asset_id || vendor.id,
      type: this.mapAssetType(vendor.asset_type),
      displayName: vendor.name || vendor.hostname,
      status: this.mapStatus(vendor.status),
      owner: {
        team: vendor.owner_team || vendor.department,
        contact: vendor.owner_email
      },
      metadata: {
        age: this.calculateAge(vendor.purchase_date),
        region: vendor.location || vendor.datacenter,
        serialNumber: vendor.serial_number,
        purchaseDate: vendor.purchase_date,
        warrantyExpiry: vendor.warranty_end
      },
      compliance: {
        lastScan: vendor.last_compliance_scan,
        policyStatus: vendor.policy_violations?.length === 0 ? 'compliant' : 'non-compliant'
      }
    };

    this.validator.validate(normalized);
    return normalized;
  }

  private mapAssetType(vendorType: string): string {
    const typeMap: Record<string, string> = {
      'server': 'physical_server',
      'vm': 'virtual_machine',
      'laptop': 'endpoint',
      'desktop': 'endpoint'
    };
    return typeMap[vendorType.toLowerCase()] || 'unknown';
  }

  private mapStatus(vendorStatus: string): string {
    const statusMap: Record<string, string> = {
      'active': 'active',
      'running': 'active',
      'stopped': 'inactive',
      'decommissioned': 'retired'
    };
    return statusMap[vendorStatus.toLowerCase()] || 'unknown';
  }

  private calculateAge(purchaseDate: string): string {
    const years = (Date.now() - new Date(purchaseDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    return \`\${years.toFixed(1)} yrs\`;
  }
}`,

  actionWrappers: `// Generated Action Wrappers - Idempotent Operations
// Each action is atomic, audited, and compensatable

import { ActionResult, AuditEmitter, IdempotencyKey } from '@vedaya/sdk';

export class AssetActions {
  private audit: AuditEmitter;

  constructor(audit: AuditEmitter) {
    this.audit = audit;
  }

  // Schedule hardware refresh
  async scheduleRefresh(
    assetId: string,
    targetDate: Date,
    idempotencyKey: IdempotencyKey
  ): Promise<ActionResult> {
    const result = await this.executeWithIdempotency(idempotencyKey, async () => {
      // Create refresh ticket
      // Update asset status to 'refresh_scheduled'
      // Notify owner
      return { success: true, ticketId: 'REF-2026-001' };
    });

    this.audit.emit({
      action: 'schedule_refresh',
      target: assetId,
      result: result.success ? 'success' : 'failure',
      idempotencyKey: idempotencyKey.value
    });

    return result;
  }

  // Reclaim SaaS license
  async reclaimLicense(
    licenseId: string,
    userId: string,
    idempotencyKey: IdempotencyKey
  ): Promise<ActionResult> {
    const result = await this.executeWithIdempotency(idempotencyKey, async () => {
      // Revoke user access
      // Update license count
      // Notify user and manager
      return { success: true, reclaimedAt: new Date().toISOString() };
    });

    this.audit.emit({
      action: 'reclaim_license',
      target: \`\${licenseId}:\${userId}\`,
      result: result.success ? 'success' : 'failure',
      idempotencyKey: idempotencyKey.value
    });

    return result;
  }

  // Create policy exception
  async createException(
    assetId: string,
    reason: string,
    expiryDate: Date,
    owner: string,
    idempotencyKey: IdempotencyKey
  ): Promise<ActionResult> {
    const result = await this.executeWithIdempotency(idempotencyKey, async () => {
      // Record exception
      // Update asset compliance status
      // Schedule review reminder
      return { success: true, exceptionId: 'EXC-2026-001' };
    });

    this.audit.emit({
      action: 'create_exception',
      target: assetId,
      result: result.success ? 'success' : 'failure',
      metadata: { reason, expiryDate: expiryDate.toISOString(), owner },
      idempotencyKey: idempotencyKey.value
    });

    return result;
  }

  private async executeWithIdempotency<T>(
    key: IdempotencyKey,
    operation: () => Promise<T>
  ): Promise<T> {
    const existing = await key.checkExisting();
    if (existing) return existing as T;
    const result = await operation();
    await key.store(result);
    return result;
  }
}`,

  sdkContract: `// Connector SDK Contract - Asset Service
// All connectors must implement these methods

export interface AssetConnector {
  // Discovery - enumerate all assets from source
  discover(): Promise<RawAsset[]>;
  
  // Normalization - transform to Vedaya schema  
  normalize(raw: RawAsset): NormalizedAsset;
  
  // Action execution - perform operations
  act(action: AssetAction): Promise<ActionResult>;
  
  // Audit emission - structured logging
  audit(event: AuditEvent): void;
}

// Template Labels for connector generation
export const CONNECTOR_TEMPLATES = [
  'list-sync',      // Full list synchronization
  'delta-sync',     // Incremental changes only
  'webhook-ingest', // Real-time event ingestion
  'reconcile',      // Drift detection and correction
  'deprovision'     // Asset retirement workflow
];`
};

// Workflow Layer Artifacts
export const workflowTemplates = {
  hardwareRefresh: `# Hardware Refresh Workflow - Temporal Style
# Generated from Change Spec CS-2026-0090

workflow:
  name: hardware-refresh-workflow
  version: "1.0"
  timeout: 4h
  
  inputs:
    targets:
      - db-server-01
      - app-server-12
      - legacy-web-03
    maintenance_window: "2026-01-15T02:00:00Z"
    dry_run: true

  steps:
    - id: validate_targets
      activity: ValidateAssetTargets
      input:
        asset_ids: "{{ inputs.targets }}"
      retries: 3
      timeout: 5m

    - id: check_maintenance_window
      activity: ValidateMaintenanceWindow
      input:
        window_start: "{{ inputs.maintenance_window }}"
        required_duration: 2h
      timeout: 1m

    - id: approval_gate
      activity: RequestApprovals
      input:
        approvers:
          - role: "Data Team Lead"
            required: true
          - role: "Platform Lead"  
            required: true
          - role: "Web Team Lead"
            required: true
      timeout: 72h
      on_timeout: escalate

    - id: create_backups
      activity: CreateAssetBackups
      input:
        asset_ids: "{{ inputs.targets }}"
      retries: 3
      timeout: 30m
      compensation: DeleteBackups

    - id: migrate_workloads
      activity: MigrateWorkloads
      input:
        source_assets: "{{ inputs.targets }}"
        strategy: blue_green
      retries: 2
      timeout: 2h
      compensation: RollbackMigration
      checkpoint: true

    - id: update_asset_status
      activity: UpdateAssetStatus
      input:
        asset_ids: "{{ inputs.targets }}"
        new_status: "refresh_in_progress"
      retries: 3
      timeout: 5m

    - id: verify_migration
      activity: VerifyMigrationSuccess
      input:
        asset_ids: "{{ inputs.targets }}"
        checks:
          - data_integrity
          - service_health
          - performance_baseline
      timeout: 15m

    - id: create_evidence_bundle
      activity: GenerateEvidenceBundle
      input:
        workflow_run_id: "{{ workflow.run_id }}"
        include:
          - approvals
          - audit_events
          - test_results
          - backup_manifests
      timeout: 5m

  on_failure:
    - activity: NotifyOnCall
      input:
        channel: "#asset-ops"
        severity: high
    - activity: CreateEscalationTicket
      input:
        queue: "Asset Management"
        priority: P1

  on_success:
    - activity: NotifyStakeholders
      input:
        channels:
          - "#asset-ops"
          - owners
    - activity: UpdateCMDB
      input:
        asset_ids: "{{ inputs.targets }}"
        status: "refreshed"`,

  saasReclaim: `# SaaS License Reclamation Workflow - Temporal Style
# Generated from Change Spec CS-2026-0092

workflow:
  name: saas-license-reclaim
  version: "1.0"
  timeout: 168h  # 7 days for notification period
  
  inputs:
    applications:
      - name: Figma
        inactive_users: 133
      - name: Notion
        inactive_users: 266
      - name: Miro
        inactive_users: 109
    notification_period_days: 7
    dry_run: true

  steps:
    - id: identify_inactive_users
      activity: QueryInactiveUsers
      input:
        applications: "{{ inputs.applications }}"
        inactive_threshold_days: 30
      retries: 3
      timeout: 10m

    - id: approval_gate
      activity: RequestApprovals
      input:
        approvers:
          - role: "IT Manager"
            required: true
          - role: "Finance"
            required: true
      timeout: 48h

    - id: send_warnings
      activity: SendUserNotifications
      input:
        users: "{{ steps.identify_inactive_users.output.users }}"
        template: license_reclaim_warning
        response_deadline_days: "{{ inputs.notification_period_days }}"
      retries: 3
      timeout: 30m

    - id: wait_for_responses
      activity: WaitForUserResponses
      input:
        deadline: "{{ steps.send_warnings.output.deadline }}"
      timeout: 168h
      checkpoint: true

    - id: filter_non_responders
      activity: FilterNonResponders
      input:
        notified_users: "{{ steps.send_warnings.output.users }}"
        responses: "{{ steps.wait_for_responses.output.responses }}"
      timeout: 5m

    - id: reclaim_licenses
      activity: ReclaimLicenses
      input:
        users: "{{ steps.filter_non_responders.output.users }}"
        dry_run: "{{ inputs.dry_run }}"
      retries: 3
      timeout: 1h
      compensation: RestoreLicenses

    - id: update_inventory
      activity: UpdateLicenseInventory
      input:
        applications: "{{ inputs.applications }}"
        reclaimed_count: "{{ steps.reclaim_licenses.output.count }}"
      retries: 3
      timeout: 5m

    - id: create_evidence_bundle
      activity: GenerateEvidenceBundle
      input:
        workflow_run_id: "{{ workflow.run_id }}"
        include:
          - approvals
          - user_notifications
          - reclaim_actions
          - cost_savings
      timeout: 5m

  on_success:
    - activity: NotifyFinance
      input:
        savings: "{{ steps.reclaim_licenses.output.annual_savings }}"
    - activity: UpdateCostDashboard`
};

// Guardrails Layer Artifacts  
export const policyControls = {
  opaRules: `# Asset Policy Rules - OPA Rego
# Control Library for Asset Intelligence

package vedaya.asset.policy

# Hardware Age Policy
hardware_age_violation[asset] {
    asset := input.assets[_]
    asset.type == "physical_server"
    years := time.parse_duration_ns(asset.age) / (365 * 24 * 60 * 60 * 1000000000)
    years > 3
}

# Hardware Age Finding
hardware_age_finding = {
    "policy": "ASSET-001",
    "name": "Hardware Age Policy",
    "description": "Physical servers must be refreshed within 3 years",
    "severity": "medium",
    "violations": hardware_age_violation,
    "remediation": {
        "action": "schedule_refresh",
        "automated": true
    }
}

# SaaS Utilization Policy
saas_underutilized[app] {
    app := input.saas_applications[_]
    app.utilization < 50
}

# SaaS Utilization Finding
saas_utilization_finding = {
    "policy": "ASSET-002", 
    "name": "SaaS Utilization Policy",
    "description": "SaaS applications below 50% utilization require review",
    "severity": "low",
    "violations": saas_underutilized,
    "remediation": {
        "action": "reclaim_inactive_seats",
        "automated": true,
        "notification_period_days": 7
    }
}

# Exception Validation
valid_exception(exception) {
    exception.owner != ""
    exception.reason != ""
    exception.expiry_date != ""
    time.parse_rfc3339_ns(exception.expiry_date) > time.now_ns()
    days_until_expiry := (time.parse_rfc3339_ns(exception.expiry_date) - time.now_ns()) / (24 * 60 * 60 * 1000000000)
    days_until_expiry <= 180  # Max 6 months
}

# Policy Check Results
policy_check_results = {
    "hardware_age": hardware_age_finding,
    "saas_utilization": saas_utilization_finding,
    "overall_status": overall_status
}

overall_status = "pass" {
    count(hardware_age_violation) == 0
    count(saas_underutilized) == 0
}

overall_status = "violations_found" {
    count(hardware_age_violation) > 0
}

overall_status = "warnings" {
    count(hardware_age_violation) == 0
    count(saas_underutilized) > 0
}`,

  controlLibrary: [
    { id: 'ASSET-001', name: 'Hardware Age Policy', description: 'Physical servers refreshed within 3 years', status: 'enforced' },
    { id: 'ASSET-002', name: 'SaaS Utilization Policy', description: 'Applications above 50% utilization', status: 'enforced' },
    { id: 'ASSET-003', name: 'Encryption at Rest', description: 'All storage volumes encrypted', status: 'enforced' },
    { id: 'ASSET-004', name: 'OS Version Minimum', description: 'Supported OS versions only', status: 'enforced' },
    { id: 'ASSET-005', name: 'EDR Present', description: 'Endpoint protection on all devices', status: 'enforced' },
    { id: 'ASSET-006', name: 'MFA Required', description: 'Multi-factor authentication enabled', status: 'enforced' },
    { id: 'ASSET-007', name: 'Admin Rights Limited', description: 'Privileged access restricted', status: 'enforced' }
  ],

  policySimulationOutput: {
    timestamp: '2026-01-11T14:30:00Z',
    change_spec_id: 'CS-2026-0090',
    simulation_type: 'pre_apply',
    results: {
      policies_checked: 7,
      passed: 5,
      warnings: 1,
      violations: 1,
      details: [
        { policy: 'ASSET-001', result: 'violation', message: '3 servers exceed age limit', action_required: 'refresh' },
        { policy: 'ASSET-003', result: 'pass', message: 'All volumes encrypted' },
        { policy: 'ASSET-004', result: 'pass', message: 'OS versions supported' },
        { policy: 'ASSET-005', result: 'pass', message: 'EDR agents active' },
        { policy: 'ASSET-006', result: 'pass', message: 'MFA enabled for all users' },
        { policy: 'ASSET-007', result: 'pass', message: 'Admin access within policy' },
        { policy: 'Migration Path', result: 'warning', message: '2 servers need workload migration first' }
      ]
    },
    recommendation: 'Proceed with refresh after completing migration plan for active workloads'
  }
};

// Test Artifacts
export const testArtifacts = {
  unitTests: `# Unit Tests - Asset Normalizers
# Generated for Change Spec validation

import pytest
from vedaya.normalizers import AssetNormalizer

class TestAssetNormalizer:
    def setup_method(self):
        self.normalizer = AssetNormalizer()

    def test_normalize_server_asset(self):
        vendor_data = {
            'asset_id': 'db-server-01',
            'asset_type': 'server',
            'name': 'db-server-01',
            'status': 'active',
            'owner_team': 'Data Team',
            'purchase_date': '2021-10-15'
        }
        
        result = self.normalizer.normalize(vendor_data)
        
        assert result['id'] == 'db-server-01'
        assert result['type'] == 'physical_server'
        assert result['status'] == 'active'
        assert result['owner']['team'] == 'Data Team'
        assert '4.' in result['metadata']['age']

    def test_normalize_invalid_type_raises(self):
        vendor_data = {'asset_type': None}
        
        with pytest.raises(ValidationError):
            self.normalizer.normalize(vendor_data)

    def test_age_calculation_accuracy(self):
        # Test edge cases for age calculation
        pass`,

  contractTests: `# Contract Tests - Asset Service API
# Mock server validation against OpenAPI spec

import pytest
from vedaya.testing import ContractTest, MockServer

class TestAssetServiceContract:
    @pytest.fixture
    def mock_server(self):
        return MockServer.from_openapi('asset-service-v1.yaml')

    def test_list_assets_returns_valid_schema(self, mock_server):
        response = mock_server.get('/api/v1/assets/hardware')
        
        assert response.status_code == 200
        assert response.validates_schema('HardwareAssetList')

    def test_update_status_requires_auth(self, mock_server):
        response = mock_server.patch(
            '/api/v1/assets/db-server-01/status',
            headers={},  # No auth
            json={'status': 'refresh_scheduled'}
        )
        
        assert response.status_code == 401

    def test_reclaim_license_idempotent(self, mock_server):
        idempotency_key = 'test-key-123'
        
        # First call
        response1 = mock_server.post(
            '/api/v1/licenses/figma/reclaim',
            headers={'Idempotency-Key': idempotency_key},
            json={'userId': 'user@example.com'}
        )
        
        # Second call with same key
        response2 = mock_server.post(
            '/api/v1/licenses/figma/reclaim',
            headers={'Idempotency-Key': idempotency_key},
            json={'userId': 'user@example.com'}
        )
        
        assert response1.json() == response2.json()`,

  workflowSimulation: `# Workflow Simulation Tests
# Dry run validation for hardware refresh

import pytest
from vedaya.workflows import WorkflowSimulator

class TestHardwareRefreshWorkflow:
    @pytest.fixture
    def simulator(self):
        return WorkflowSimulator('hardware-refresh-workflow')

    def test_dry_run_no_side_effects(self, simulator):
        result = simulator.run(
            inputs={
                'targets': ['db-server-01', 'app-server-12'],
                'dry_run': True
            }
        )
        
        assert result.status == 'completed'
        assert result.side_effects == []
        assert result.would_affect == ['db-server-01', 'app-server-12']

    def test_approval_gate_blocks_without_approvers(self, simulator):
        result = simulator.run(
            inputs={
                'targets': ['db-server-01'],
                'dry_run': False
            },
            mock_approvals=[]
        )
        
        assert result.status == 'blocked'
        assert result.blocked_at == 'approval_gate'

    def test_rollback_on_migration_failure(self, simulator):
        result = simulator.run(
            inputs={'targets': ['db-server-01']},
            inject_failure_at='migrate_workloads'
        )
        
        assert result.status == 'rolled_back'
        assert 'RollbackMigration' in result.compensation_executed`
};

// Evidence Bundle Template
export const evidenceBundleTemplate = {
  id: 'EB-2026-0150',
  name: 'Hardware Refresh Evidence - 3 Servers',
  type: 'asset_operation',
  related_change_spec: 'CS-2026-0090',
  created_at: '2026-01-11T16:00:00Z',
  created_by: 'system',
  status: 'complete',
  contents: [
    { type: 'Change Spec', description: 'CS-2026-0090 - Hardware Refresh', file: 'change-spec.json' },
    { type: 'Plan Output', description: 'Terraform plan for infrastructure changes', file: 'plan-output.txt' },
    { type: 'Policy Simulation', description: 'OPA policy check results', file: 'policy-simulation.json' },
    { type: 'Test Results', description: 'Unit, contract, and simulation test results', file: 'test-results.json' },
    { type: 'Approval Records', description: '3 approvals from team leads', file: 'approvals.json' },
    { type: 'Apply Log', description: 'Execution log from apply phase', file: 'apply-log.txt' },
    { type: 'Verify Log', description: 'Post-apply verification results', file: 'verify-log.txt' },
    { type: 'Audit Events', description: '47 audit events from workflow execution', file: 'audit-events.json' }
  ],
  total_items: 8,
  download_url: '#'
};

// Audit Event Schema
export const auditEventSchema = {
  version: '1.0',
  event: {
    id: 'string (uuid)',
    timestamp: 'string (iso8601)',
    type: 'enum (connector.action | workflow.step | policy.check | approval.granted | system.event)',
    source: 'string (component name)',
    actor: 'string (user or system)',
    action: 'string (action performed)',
    target: 'string (affected entity)',
    result: 'enum (success | failure | skipped)',
    metadata: 'object (additional context)',
    workflow_run_id: 'string (optional)',
    change_spec_id: 'string (optional)',
    idempotency_key: 'string (optional)'
  }
};

// Sample Audit Events
export const assetAuditEvents = [
  {
    id: 'AUD-20001',
    timestamp: '2026-01-11T14:30:00Z',
    type: 'workflow.step',
    source: 'Asset Workflow Engine',
    actor: 'system',
    action: 'validate_targets',
    target: 'db-server-01, app-server-12, legacy-web-03',
    result: 'success',
    workflow_run_id: 'WR-150',
    change_spec_id: 'CS-2026-0090'
  },
  {
    id: 'AUD-20002',
    timestamp: '2026-01-11T14:31:00Z',
    type: 'approval.granted',
    source: 'Approval Engine',
    actor: 'Pat Thompson (Data Team Lead)',
    action: 'approve_change',
    target: 'CS-2026-0090',
    result: 'success',
    workflow_run_id: 'WR-150'
  },
  {
    id: 'AUD-20003',
    timestamp: '2026-01-11T14:32:00Z',
    type: 'connector.action',
    source: 'Asset Service Connector',
    actor: 'system',
    action: 'update_asset_status',
    target: 'db-server-01',
    result: 'success',
    metadata: { new_status: 'refresh_scheduled' },
    idempotency_key: 'refresh-db-server-01-20260111'
  }
];

// SaaS User List (for modal)
export const saasUserLists = {
  figma: {
    app: 'Figma',
    total_users: 200,
    active_users: 67,
    inactive_users: [
      { email: 'john.doe@company.com', last_login: '45 days ago', department: 'Marketing' },
      { email: 'jane.smith@company.com', last_login: '62 days ago', department: 'Sales' },
      { email: 'bob.wilson@company.com', last_login: '38 days ago', department: 'HR' }
      // ... 130 more
    ]
  },
  notion: {
    app: 'Notion',
    total_users: 500,
    active_users: 234,
    inactive_users: [
      { email: 'alice.jones@company.com', last_login: '55 days ago', department: 'Engineering' },
      { email: 'charlie.brown@company.com', last_login: '41 days ago', department: 'Product' }
      // ... 264 more
    ]
  },
  miro: {
    app: 'Miro',
    total_users: 150,
    active_users: 41,
    inactive_users: [
      { email: 'dave.miller@company.com', last_login: '90 days ago', department: 'Design' },
      { email: 'eve.taylor@company.com', last_login: '72 days ago', department: 'UX' }
      // ... 107 more
    ]
  }
};
